import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Pencil, ExternalLink, Info, Sparkles, Target, ClipboardCheck, Share2, CalendarClock } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { StatusBadge } from "@/components/StatusBadge";
import { JobLinks } from "@/components/JobLinks";
import { PROVINCE_COLORS } from "@/lib/constants";
import { modelLabel } from "@/lib/streamMode";
import { CrsCard, EligibilityCard } from "@/pages/Results";
import { ImprovementHints } from "@/components/ImprovementHints";
import { PdfButton } from "@/components/PdfButton";
import { ShareButton } from "@/components/ShareButton";
import { DrawHistory } from "@/components/DrawHistory";
import { DrawAlertForm } from "@/components/DrawAlertForm";
import { LeadCapture } from "@/components/LeadCapture";
import { FeedbackWidget } from "@/components/FeedbackWidget";

export default function StreamResult() {
  const { streamResult, setStreamResult, profile } = useProfile();
  if (!streamResult) return <EmptyState />;
  const patch = (p) => setStreamResult({ ...streamResult, ...p });
  return <StreamReport streamResult={streamResult} profile={profile} onPatch={patch} />;
}

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });

export function StreamReport({ streamResult, profile, shared, onPatch }) {
  const { stream, crs, clb } = streamResult;
  const isCrs = stream.scoring_model === "crs_based";
  const isGrid = stream.scoring_model === "grid";

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid={shared ? "shared-report-page" : "stream-result-page"}>
      {shared ? (
        <div className="flex flex-col justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center" data-testid="shared-banner">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Share2 className="h-4 w-4 text-[#C8102E]" />
            <span>Shared stream score report · created {fmtDate(shared.created_at)}</span>
            <span className="hidden items-center gap-1 text-slate-400 sm:inline-flex"><CalendarClock className="h-3.5 w-3.5" /> link expires {fmtDate(shared.expires_at)}</span>
          </div>
          <Link to="/calculator" data-testid="shared-run-own-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#C8102E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#A60C24]">
            Run your own assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <Link to="/calculator" data-testid="stream-result-change-stream"
          className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Choose a different stream
        </Link>
      )}

      <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white"
              style={{ backgroundColor: PROVINCE_COLORS[stream.province] }}>{stream.province}</span>
            <span className="text-sm text-slate-500">{stream.province_name} · {stream.stream_group}</span>
          </div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="stream-result-name">
            {stream.name}
          </h1>
          <p className="mt-2 text-slate-600">{modelLabel({ ...stream, max_points: stream.max_points })} · English CLB {clb.min}</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <PdfButton kind="stream" result={streamResult} profile={profile} testId="stream-result-pdf-btn" dark />
          {!shared && <ShareButton result={streamResult} profile={profile} />}
          {!shared && (
            <Link to={`/assessment?stream=${stream.id}`} data-testid="stream-result-edit-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
              <Pencil className="h-4 w-4" /> Edit answers
            </Link>
          )}
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <ScoreHero stream={stream} isCrs={isCrs} />
        </div>
        <div className="lg:col-span-4">
          {isCrs ? <CrsCard crs={crs} /> : <Breakdown stream={stream} isGrid={isGrid} />}
        </div>
      </div>

      <p className="mt-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        Simplified estimate for guidance only. {stream.cutoff_note}
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7"><EligibilityCard stream={stream} /></div>
        <div className="lg:col-span-5 space-y-6">
          <ImprovementHints stream={stream} />
          {!shared && <DrawAlertForm stream={stream} assessmentId={streamResult.assessment_id} alertEmail={streamResult.alert_email} onSubscribed={(email) => onPatch({ alert_email: email, lead_email: streamResult.lead_email || email })} />}
          <DrawHistory stream={stream} />
        </div>
      </div>

      {!shared && <FeedbackWidget page="stream_result" streamId={streamResult.stream?.id} assessmentId={streamResult.assessment_id} />}
      {!shared && <LeadCapture result={streamResult} onSaved={(email) => onPatch({ lead_email: email })} />}

      <JobLinks profile={profile} />

      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to={shared ? "/calculator" : "/assessment"} data-testid="stream-result-check-all"
          className="inline-flex items-center gap-2 rounded-md bg-[#0F172A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1E293B]">
          <ClipboardCheck className="h-4 w-4" /> {shared ? "Calculate your own score" : "Check eligibility across all streams"} <ArrowRight className="h-4 w-4" />
        </Link>
        <a href={stream.official_url} target="_blank" rel="noreferrer" data-testid="stream-result-official-link"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Official program page <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

function ScoreHero({ stream, isCrs }) {
  const isEligOnly = stream.scoring_model === "eligibility";
  const max = isCrs ? 1200 : stream.max_points || 100;
  const pct = stream.score != null ? Math.min(100, Math.round((stream.score / max) * 100)) : 0;
  const meets = stream.cutoff != null && stream.score != null && stream.score >= stream.cutoff;
  const cutoffPct = stream.cutoff != null ? Math.min(100, (stream.cutoff / max) * 100) : null;
  const gap = stream.cutoff != null && stream.score != null ? stream.cutoff - stream.score : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-[#0F172A] p-6 text-white shadow-sm sm:p-8" data-testid="stream-score-hero">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          <Target className="h-5 w-5 text-[#F87171]" /> Your estimated score
        </span>
        <StatusBadge status={stream.eligibility_status} testId="stream-result-status" />
      </div>

      {isEligOnly ? (
        <div className="mt-6 rounded-lg bg-white/5 p-5 text-slate-200">
          <Sparkles className="mb-2 h-5 w-5 text-slate-400" />
          This is an eligibility-based stream — there is no ranking score. Your result depends entirely on meeting the criteria listed below.
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1">
            <span className="font-mono-tech text-6xl font-bold leading-none" data-testid="stream-result-score">{stream.score}</span>
            <span className="mb-1 text-lg text-slate-400">{isCrs ? "CRS / 1200" : `/ ${stream.max_points}`}</span>
          </div>
          <div className="relative mt-5 h-3 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: meets ? "#22C55E" : "#F87171" }} />
            {cutoffPct != null && <div className="absolute top-0 h-full w-0.5 bg-white" style={{ left: `${cutoffPct}%` }} />}
          </div>
          {stream.cutoff != null && (
            <p className="mt-3 text-sm text-slate-300" data-testid="stream-result-cutoff-msg">
              Recent cutoff ~<strong className="text-white">{stream.cutoff}</strong>.{" "}
              {meets
                ? <span className="text-emerald-400">You are {stream.score - stream.cutoff} point{stream.score - stream.cutoff === 1 ? "" : "s"} above the cutoff.</span>
                : <span className="text-[#FCA5A5]">You are {gap} point{gap === 1 ? "" : "s"} below the cutoff.</span>}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Breakdown({ stream, isGrid }) {
  return (
    <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="stream-result-breakdown">
      <h3 className="font-heading text-base font-semibold text-slate-900">Point breakdown</h3>
      {!isGrid || stream.breakdown.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No point grid applies to this stream.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {stream.breakdown.map((b, i) => (
            <li key={b.label} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <span className="text-slate-600">{b.label}</span>
              <span className="font-mono-tech font-semibold text-slate-900">{b.points}</span>
            </li>
          ))}
          <li className="flex items-center justify-between gap-3 pt-3 text-sm font-bold">
            <span className="text-slate-900">Total</span>
            <span className="font-mono-tech text-[#C8102E]">{stream.score} / {stream.max_points}</span>
          </li>
        </ul>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FEF2F2] text-[#C8102E]">
        <Target className="h-8 w-8" />
      </div>
      <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">No stream score yet</h1>
      <p className="mt-2 text-slate-600">Pick a stream and answer its questions to see your detailed score.</p>
      <Link to="/calculator" data-testid="stream-result-empty-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#C8102E] px-6 py-3 text-sm font-semibold text-white hover:bg-[#A60C24]">
        Choose a stream <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
